import {useEffect, useRef} from 'react'
import {useEinsteinRecentlyViewed} from '../../../../../hooks/use-einstein-custom'
import {useEnrichVickyProducts} from '../../../../../hooks/use-enrich-vicky-products'

import {isRecentlyViewedSource} from './helpers'

/**
 * Converts the "Recently Viewed" carousel dynamic source into renderable product tiles.
 *
 * When `dynamicContent` is a recently viewed source label, the hook calls the
 * custom Einstein recently viewed endpoint once for that source and maps the
 * returned products to `GDIProductTileBlock` pseudo-tiles. Non-recently viewed
 * dynamic sources return an empty list and do not fetch.
 *
 * @param {?string} dynamicContent - CMS dynamic source label for the carousel.
 * @param {object} [options] - `availabilityEnabled` (default true) gates the live stock fetch.
 * @returns {Array<object>} Product tile definitions consumable by `renderComponentsSwitch`.
 */
export const useRecentlyViewedTiles = (dynamicContent, options = {}) => {
    const {availabilityEnabled = true} = options
    const shouldFetch = isRecentlyViewedSource(dynamicContent)
    const fetchedSourceRef = useRef(null)
    const {products, getRecentlyViewed} = useEinsteinRecentlyViewed()

    useEffect(() => {
        if (!shouldFetch) {
            fetchedSourceRef.current = null
            return
        }

        if (fetchedSourceRef.current === dynamicContent) return

        fetchedSourceRef.current = dynamicContent
        getRecentlyViewed({shouldFetch})
    }, [dynamicContent, getRecentlyViewed, shouldFetch])

    /* Einstein returns variationGroup-shaped products (single large image,
     * no per-color swatches). Reshape against the master so the tile gets
     * full image groups, variants, swatches, QuickView and color-aware URL
     * just like on PLP. Hook must run unconditionally for hooks rules. */
    const enrichedProducts = useEnrichVickyProducts(products, {
        enabled: shouldFetch,
        availabilityEnabled
    })

    if (!shouldFetch) return []

    return (enrichedProducts || []).map((product) => ({
        __typename: 'GDIProductTileBlock',
        Product: product,
        ContentLink: {GuidValue: `recently-viewed-${product.id}`}
    }))
}
